/**
 * v3.16-1238 — Reusable Activity Completion Dialog.
 *
 * NOT Ghost-specific. Any operational activity (appointment, phone call,
 * follow-up, task, manual Ghost with require_completion_details) can
 * plug this in to satisfy the Hub's mandatory outcome/notes contract.
 *
 * Contract:
 *   • The consumer fetches the rule (or already has it) and passes it
 *     as `rule`. If `rule` is omitted, the dialog fetches it via
 *     `ruleEndpoint` (GET → { rule: {...} }).
 *   • The consumer supplies `onSubmit(payload) → Promise` — the dialog
 *     does NOT know which HTTP endpoint completes the activity. This
 *     keeps the component reusable across resources.
 *   • Local validation mirrors the server's `validate_payload`. Server-
 *     side 422 detail is surfaced verbatim if the local validation
 *     misses anything (defence-in-depth).
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { AlertCircle, CheckCircle2 } from 'lucide-react';


const DEFAULT_TITLE = 'Complete activity';
const DEFAULT_DESCRIPTION =
  'Record the outcome and notes before closing this item.';


export const ActivityCompletionDialog = ({
  open,
  onOpenChange,
  rule: providedRule,      // optional: preloaded CompletionRule
  ruleEndpoint,            // optional: e.g. `/ghost-items/{id}/completion-rule`
  onSubmit,                // async (payload) => void ; must throw on failure
  itemTitle,
  itemSubtitle,
  // v3.18-1351 GHOST-CONTEXT — optional rich client context rendered
  // above the outcome/notes form. Every operator-facing identifier
  // the rep needs to know WHO the task is for before completing it.
  // Shape: {
  //   id, name, company, rep_name, phone, email, stage, deal_amount,
  //   funding_company, due_label, task_type,
  //   onOpenClient?: () => void
  // }
  contextClient,
  submitLabel = 'Mark complete',
  cancelLabel = 'Cancel',
  testIdPrefix = 'activity-completion',
}) => {
  const [rule, setRule] = useState(providedRule || null);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});   // { outcome, notes, top? }
  const [loadingRule, setLoadingRule] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOutcome(''); setNotes(''); setErrors({}); setSubmitting(false);
    if (providedRule) { setRule(providedRule); return; }
    if (!ruleEndpoint) return;
    let cancelled = false;
    setLoadingRule(true);
    (async () => {
      try {
        const res = await api.get(ruleEndpoint);
        if (!cancelled) setRule(res?.data?.rule || null);
      } catch (err) {
        if (!cancelled) {
          setErrors({ top: 'Could not load completion rule.' });
        }
      } finally {
        if (!cancelled) setLoadingRule(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, providedRule, ruleEndpoint]);

  const requiresOutcome = !!rule?.requires_outcome;
  const requiresNotes   = !!rule?.requires_notes;
  const outcomeOptions  = rule?.outcome_options || null;
  const outcomeLabel    = rule?.outcome_label || 'Outcome';
  const notesLabel      = rule?.notes_label || 'Notes';
  const notesMin        = rule?.notes_min_length || 0;

  const localValidate = () => {
    const next = {};
    const o = (outcome || '').trim();
    const n = (notes || '').trim();
    if (requiresOutcome && !o) next.outcome = `${outcomeLabel} is required.`;
    if (requiresNotes && !n) next.notes = `${notesLabel} are required.`;
    else if (requiresNotes && n.length < notesMin) {
      next.notes = `${notesLabel} must be at least ${notesMin} characters.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!localValidate()) return;
    setSubmitting(true);
    try {
      await onSubmit({ outcome: outcome.trim() || null, notes: notes.trim() || null });
      onOpenChange(false);
    } catch (err) {
      // Surface server-side structured errors.
      const detail = err?.response?.data?.detail;
      if (detail?.errors && Array.isArray(detail.errors)) {
        const next = {};
        detail.errors.forEach((e) => {
          if (e.field) next[e.field] = e.message;
          else next.top = e.message;
        });
        setErrors(next);
      } else {
        const msg = detail?.message || detail || err?.message || 'Failed to complete.';
        setErrors({ top: typeof msg === 'string' ? msg : 'Failed to complete.' });
        toast.error(typeof msg === 'string' ? msg : 'Failed to complete.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showLoader = loadingRule && !rule;

  const readyToSubmit = useMemo(() => {
    if (!rule) return false;
    if (requiresOutcome && !(outcome || '').trim()) return false;
    if (requiresNotes && (notes || '').trim().length < notesMin) return false;
    return true;
  }, [rule, requiresOutcome, requiresNotes, outcome, notes, notesMin]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle data-testid={`${testIdPrefix}-title`}>
            {itemTitle || DEFAULT_TITLE}
          </DialogTitle>
          <DialogDescription data-testid={`${testIdPrefix}-description`}>
            {itemSubtitle || DEFAULT_DESCRIPTION}
          </DialogDescription>
        </DialogHeader>

        {showLoader ? (
          <div className="py-6 text-center text-sm text-slate-500">Loading rule…</div>
        ) : (
          <div className="space-y-4 py-2">
            {errors.top && (
              <div
                data-testid={`${testIdPrefix}-error-top`}
                className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{errors.top}</span>
              </div>
            )}

            {/* v3.18-1351 GHOST-CONTEXT — Client context header. Rendered
                above the outcome/notes form so the operator sees WHO
                the task is for BEFORE they type an outcome. Every
                identifier the rep needs is stacked here — Name /
                Company / Rep / Phone / Email / Stage / Deal Amount /
                Funding Company / Due — plus a prominent "Open Client
                Record" jump button so Ghost stays the command center. */}
            {contextClient && contextClient.name && (
              <div
                data-testid={`${testIdPrefix}-context-client`}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Client
                    </div>
                    <div
                      className="text-base font-extrabold text-slate-900 truncate"
                      data-testid={`${testIdPrefix}-context-client-name`}
                    >
                      {contextClient.name}
                    </div>
                  </div>
                  {contextClient.onOpenClient && (
                    <button
                      type="button"
                      onClick={contextClient.onOpenClient}
                      data-testid={`${testIdPrefix}-open-client-btn`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md
                                 bg-indigo-600 hover:bg-indigo-700 text-white
                                 px-2.5 py-1.5 text-xs font-semibold shadow-sm"
                    >
                      Open Client Record
                    </button>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                  {contextClient.company && (
                    <>
                      <dt className="text-slate-500">Company:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-company`}>
                        {contextClient.company}
                      </dd>
                    </>
                  )}
                  {contextClient.rep_name && (
                    <>
                      <dt className="text-slate-500">Representative:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-rep`}>
                        {contextClient.rep_name}
                      </dd>
                    </>
                  )}
                  {contextClient.phone && (
                    <>
                      <dt className="text-slate-500">Phone:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-phone`}>
                        {contextClient.phone}
                      </dd>
                    </>
                  )}
                  {contextClient.email && (
                    <>
                      <dt className="text-slate-500">Email:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-email`}>
                        {contextClient.email}
                      </dd>
                    </>
                  )}
                  {contextClient.due_label && (
                    <>
                      <dt className="text-slate-500">
                        {contextClient.task_type ? 'Due:' : 'Appointment:'}
                      </dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-due`}>
                        {contextClient.due_label}
                      </dd>
                    </>
                  )}
                  {contextClient.stage && (
                    <>
                      <dt className="text-slate-500">Current Stage:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-stage`}>
                        {contextClient.stage}
                      </dd>
                    </>
                  )}
                  {contextClient.deal_amount && (
                    <>
                      <dt className="text-slate-500">Loan Amount:</dt>
                      <dd className="font-semibold text-emerald-700" data-testid={`${testIdPrefix}-context-deal-amount`}>
                        {contextClient.deal_amount}
                      </dd>
                    </>
                  )}
                  {contextClient.funding_company && (
                    <>
                      <dt className="text-slate-500">Funding Company:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-funding`}>
                        {contextClient.funding_company}
                      </dd>
                    </>
                  )}
                  {contextClient.task_type && (
                    <>
                      <dt className="text-slate-500">Task:</dt>
                      <dd className="font-semibold text-slate-800 truncate" data-testid={`${testIdPrefix}-context-task-type`}>
                        {contextClient.task_type}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            )}

            {/* Outcome */}
            {(requiresOutcome || outcomeOptions) && (
              <div>
                <Label className="text-sm">
                  {outcomeLabel}
                  {requiresOutcome && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {outcomeOptions ? (
                  <Select value={outcome} onValueChange={setOutcome}>
                    <SelectTrigger
                      data-testid={`${testIdPrefix}-outcome-trigger`}
                      className={errors.outcome ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Select outcome…" />
                    </SelectTrigger>
                    <SelectContent>
                      {outcomeOptions.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input
                    type="text"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    data-testid={`${testIdPrefix}-outcome-input`}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${errors.outcome ? 'border-red-500' : 'border-slate-300'}`}
                    placeholder="Outcome"
                    autoFocus
                  />
                )}
                {errors.outcome && (
                  <p data-testid={`${testIdPrefix}-outcome-error`}
                     className="text-xs text-red-600 mt-1">{errors.outcome}</p>
                )}
              </div>
            )}

            {/* Notes */}
            {(requiresNotes || rule) && (
              <div>
                <Label className="text-sm">
                  {notesLabel}
                  {requiresNotes && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid={`${testIdPrefix}-notes`}
                  className={errors.notes ? 'border-red-500' : ''}
                  rows={4}
                  placeholder={requiresNotes ? `At least ${notesMin} characters…` : 'Optional context'}
                />
                {errors.notes && (
                  <p data-testid={`${testIdPrefix}-notes-error`}
                     className="text-xs text-red-600 mt-1">{errors.notes}</p>
                )}
              </div>
            )}

            {!requiresOutcome && !requiresNotes && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                <CheckCircle2 className="h-4 w-4" />
                No outcome or notes required for this activity.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid={`${testIdPrefix}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || showLoader || !readyToSubmit}
            data-testid={`${testIdPrefix}-submit`}
            className="bg-accent hover:bg-gold-dark text-white"
          >
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityCompletionDialog;
